import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { TellAFriend } from "@/components/TellAFriend";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Star, Route, QrCode, ShieldCheck, Camera, Navigation, Tag, Shield, Megaphone, Users, ShoppingBag, Gift, Clock } from "lucide-react";
import { LOGO_URL } from "@/lib/constants";

function AdminSignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin" && !(u.permissions && u.permissions.length)) {
        toast.error("This account is not an administrator");
      } else {
        toast.success("Welcome, admin");
        navigate("/admin");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sign in failed");
    } finally { setLoading(false); }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="footer-admin-link" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-400 hover:text-ys-sky transition-colors">
          <Shield className="w-3.5 h-3.5" /> Administrator sign in
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="admin-signin-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Shield className="w-5 h-5 text-ys-sky" />Administrator sign in</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">Back-end management only — not for buying or selling.</p>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div><Label>Admin email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className="mt-1.5 rounded-xl h-11" required /></div>
          <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className="mt-1.5 rounded-xl h-11" required /></div>
          <DialogFooter><Button type="submit" disabled={loading} data-testid="admin-signin-submit" className="w-full rounded-full h-11 bg-slate-900 hover:bg-slate-800">{loading ? "Signing in…" : "Sign in to admin"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1758779529326-f29d7cbfeab1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  clocks: "https://images.unsplash.com/photo-1662623646616-16247b3f26b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  house: "https://images.unsplash.com/photo-1605146769289-440113cc3d00?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const FEATURES = [
  { icon: MapPin, title: "Live Sale Map", desc: "Every current yard sale drops a pin on the map. Tap a pin, open the sale." },
  { icon: Navigation, title: "GPS Sales Near Me", desc: "Turn on your location to see the sales you're closest to right now." },
  { icon: Route, title: "Route Planner", desc: "Pick the sales you want and we plot the smartest driving order." },
  { icon: Camera, title: "5-Photo Listings", desc: "Sellers show off their best finds with a photo gallery and a cover shot." },
  { icon: Star, title: "Shopper Ratings", desc: "Real ratings from real shoppers help you skip the duds." },
  { icon: QrCode, title: "Tell a Friend", desc: "Share any sale by link or on-screen QR code in one tap." },
];

function WhatsNew() {
  const [items, setItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/announcements").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/site-reviews").then(({ data }) => setReviews(data)).catch(() => {});
  }, []);
  if (!items.length && !reviews.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="whats-new-section">
      {items.length > 0 && (<>
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 text-ys-sky text-xs font-mono uppercase tracking-widest font-semibold"><Megaphone className="w-3.5 h-3.5" /> What's New</span>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-4">Fresh from the Yard-skip team</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {items.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl p-6 border border-slate-100" data-testid={`landing-ann-${a.id}`}>
              <p className="text-xs font-mono uppercase tracking-widest text-ys-sky">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "New"}</p>
              <h3 className="font-display text-lg font-semibold mt-1">{a.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>
      </>)}
      {reviews.length > 0 && (
        <div className="mt-12">
          <div className="text-center"><h2 className="font-display text-2xl font-bold">Loved by our members</h2></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {reviews.slice(0, 6).map((r) => (
              <div key={r.id} className="bg-white rounded-2xl p-6 border border-slate-100" data-testid={`landing-review-${r.id}`}>
                <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.stars ? "fill-ys-gold text-ys-gold" : "text-slate-200"}`} />)}</div>
                <p className="text-slate-700 mt-2 text-sm">"{r.comment}"</p>
                <p className="text-xs text-slate-400 mt-2">— {r.user_name}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function StatsBar() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  const stats = [
    { key: "signed_up", label: "Members signed up", value: s.signed_up, Icon: Users },
    { key: "selling", label: "Selling now", value: s.selling, Icon: Tag },
    { key: "browsing", label: "Shopping now", value: s.browsing, Icon: ShoppingBag },
  ];
  return (
    <div className="bg-slate-900 text-white" data-testid="stats-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-center sm:justify-between gap-x-8 gap-y-2">
        <div className="flex items-center gap-6 sm:gap-8">
          {stats.map(({ key, label, value, Icon }) => (
            <div key={key} className="flex items-center gap-2" data-testid={`stat-${key}`}>
              <Icon className="w-4 h-4 text-ys-sky" />
              <span className="font-display font-extrabold text-lg tabular-nums" data-testid={`stat-${key}-value`}>{value.toLocaleString()}</span>
              <span className="text-xs text-slate-300 uppercase tracking-wide hidden sm:inline">{label}</span>
              <span className="text-xs text-slate-300 sm:hidden">{label.split(" ")[0]}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2" data-testid="stat-rating">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`w-4 h-4 ${n <= Math.round(s.rating_avg) ? "fill-ys-gold text-ys-gold" : "text-slate-600"}`} />
            ))}
          </div>
          <span className="font-display font-extrabold text-lg tabular-nums" data-testid="stat-rating-value">
            {s.rating_count ? s.rating_avg.toFixed(1) : "New"}
          </span>
          <span className="text-xs text-slate-300 uppercase tracking-wide">
            {s.rating_count ? `site rating · ${s.rating_count} review${s.rating_count === 1 ? "" : "s"}` : "site rating"}
          </span>
        </div>
      </div>
    </div>
  );
}

function TrialBanner({ navigate }) {
  return (
    <div className="bg-gradient-to-r from-ys-gold to-amber-400 text-slate-900" data-testid="trial-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
        <span className="inline-flex items-center gap-2 font-semibold text-sm sm:text-base">
          <Gift className="w-5 h-5 shrink-0" /> First time here? Your first weekend is <strong>free</strong> — browse &amp; sell this <strong>Friday–Sunday</strong>, no card needed.
        </span>
        <Button onClick={() => navigate("/register")} data-testid="trial-banner-cta" className="rounded-full h-9 bg-slate-900 text-white hover:bg-slate-800">Start free trial</Button>
      </div>
    </div>
  );
}

function PromoBanner({ promo, navigate }) {
  const end = promo.end ? new Date(promo.end) : null;
  const days = end ? Math.ceil((end.getTime() - Date.now()) / 86400000) : null;
  const countdown = days == null || days < 0 ? null : days === 0 ? "Ends today" : `Ends in ${days} day${days === 1 ? "" : "s"}`;
  return (
    <div className="bg-gradient-to-r from-ys-gold to-amber-400 text-slate-900" data-testid="promo-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
        <span className="inline-flex items-center gap-2 font-semibold text-sm sm:text-base">
          <Gift className="w-5 h-5 shrink-0" /> {promo.title || "Limited-time FREE access!"} — {promo.message || "Browse, Sell & Buy on Yard-skip for free. No card needed."}
        </span>
        {countdown && <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-xs font-bold px-3 py-1" data-testid="promo-countdown"><Clock className="w-3.5 h-3.5" />{countdown}</span>}
        <Button onClick={() => navigate("/register")} data-testid="promo-banner-cta" className="rounded-full h-9 bg-slate-900 text-white hover:bg-slate-800">Claim free access</Button>
      </div>
    </div>
  );
}

function PromoEndedBanner({ navigate }) {
  return (
    <div className="bg-slate-900 text-white" data-testid="promo-ended-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
        <span className="font-semibold text-sm sm:text-base">Our free promotion has ended — pick a plan to keep enjoying Yard-skip.</span>
        <Button onClick={() => navigate("/pricing")} className="rounded-full h-9 bg-ys-sky hover:bg-sky-700" data-testid="promo-ended-banner-cta">See plans</Button>
      </div>
    </div>
  );
}

function SiteReviewButton() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const submit = async () => {
    if (!stars) { toast.error("Pick a star rating"); return; }
    try {
      await api.post("/site-reviews", { stars, comment });
      toast.success("Thanks for rating Yard-skip!");
      setOpen(false); setComment(""); setStars(0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit review");
    }
  };
  if (!user) {
    return (
      <Button variant="outline" data-testid="rate-site-btn" onClick={() => { toast.info("Sign in to rate the site"); navigate("/register"); }}
        className="rounded-full gap-2 border-slate-300"><Star className="w-4 h-4 text-ys-gold" />Rate this site</Button>
    );
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="rate-site-btn" className="rounded-full gap-2 border-slate-300"><Star className="w-4 h-4 text-ys-gold" />Rate this site</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="site-review-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Star className="w-5 h-5 text-ys-gold" />Rate Yard-skip</DialogTitle></DialogHeader>
        <div className="flex gap-1" data-testid="rate-site-stars">
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} type="button" onClick={() => setStars(s)} data-testid={`rate-star-${s}`}>
              <Star className={`w-8 h-8 ${s <= stars ? "fill-ys-gold text-ys-gold" : "text-slate-300"}`} />
            </button>
          ))}
        </div>
        <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What do you love about Yard-skip?" data-testid="rate-site-comment" className="rounded-xl" />
        <DialogFooter><Button onClick={submit} data-testid="rate-site-submit" className="rounded-full bg-ys-sky hover:bg-sky-700">Post review</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [promo, setPromo] = useState(null);
  useEffect(() => { api.get("/promo").then(({ data }) => setPromo(data)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen bg-ys-canvas">
      <Header />
      {promo?.active ? <PromoBanner promo={promo} navigate={navigate} />
        : promo?.ended ? <PromoEndedBanner navigate={navigate} />
        : !user && <TrialBanner navigate={navigate} />}
      <StatsBar />

      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="ys-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 text-ys-sky text-xs font-mono uppercase tracking-widest font-semibold">
              <MapPin className="w-3.5 h-3.5" /> Neighborhood garage sales
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-5 leading-[1.05]">
              Find the sale.<br /><span className="text-ys-sky">Skip</span> the search.
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-lg leading-relaxed">
              Yard-skip puts every local garage sale on one live map. Shoppers browse, route, and rate.
              Sellers post in minutes with photos, a map pin, and sale hours.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={() => navigate("/browse")} data-testid="hero-browse-btn"
                className="rounded-full h-12 px-7 text-base bg-ys-sky hover:bg-sky-700 gap-2">
                <MapPin className="w-5 h-5" /> Browse the Map
              </Button>
              <Button onClick={() => navigate("/register")} data-testid="hero-sell-btn" variant="outline"
                className="rounded-full h-12 px-7 text-base gap-2 border-slate-300">
                <Tag className="w-5 h-5" /> Post a Sale
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-500" /> Cancel anytime</span>
              <span className="flex items-center gap-1.5"><Tag className="w-4 h-4 text-ys-gold" /> From $5</span>
            </div>
          </div>
          <div className="relative ys-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative rounded-[2rem] shadow-2xl w-full h-[460px] sm:h-[640px] bg-white flex items-center justify-center p-4">
            <div className="absolute -inset-4 bg-sky-200/40 rounded-[2rem] blur-2xl -z-10" />
            <img src={LOGO_URL} alt="Yard-skip" className="max-w-full max-h-full object-contain" data-testid="landing-logo-graphic" /></div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Everything a treasure hunter needs</h2>
          <p className="text-slate-600 mt-3">Built mobile-first for the road, and just as sharp on desktop.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center mb-4">
                <f.icon className="w-6 h-6 text-ys-sky" />
              </div>
              <h3 className="font-display text-lg font-semibold">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-[2rem] bg-white border border-slate-200 shadow-xl text-slate-900 p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center overflow-hidden relative">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Two simple sides. One map.</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="bg-sky-50 border border-sky-100 rounded-2xl p-5">
                <p className="font-mono text-xs uppercase tracking-widest text-ys-sky font-semibold">Shoppers</p>
                <p className="text-3xl font-extrabold mt-1 text-slate-900">$5<span className="text-base font-medium text-slate-600">/week</span></p>
                <p className="text-sm text-slate-600 mt-1">or $10/mo unlimited</p>
              </div>
              <div className="bg-sky-50 border border-sky-100 rounded-2xl p-5">
                <p className="font-mono text-xs uppercase tracking-widest text-ys-sky font-semibold">Sellers</p>
                <p className="text-3xl font-extrabold mt-1 text-slate-900">$5<span className="text-base font-medium text-slate-600">/week</span></p>
                <p className="text-sm text-slate-600 mt-1">or $25/mo Pro Seller</p>
                <p className="text-xs text-ys-sky font-semibold mt-1">Includes all shopper options too</p>
              </div>
            </div>
            <Button onClick={() => navigate("/pricing")} data-testid="cta-pricing-btn"
              className="mt-6 rounded-full bg-ys-sky text-white hover:bg-sky-700 h-12 px-7">See all plans</Button>
            <p className="text-sm text-slate-600 mt-3" data-testid="pro-seller-note">The <strong>Pro Seller</strong> plan ($25/mo) is all-inclusive — it gives you every <strong>shopper</strong> browsing option <em>plus</em> the full month of <strong>seller</strong> listing tools.</p>
          </div>
          <div className="flex justify-center">
            <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_yard-sale-finder-4/artifacts/p4jkr1jc_yard.jfif" alt="Garage sale finds" className="rounded-2xl w-full max-w-sm h-72 object-cover shadow-xl" data-testid="two-sides-image" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="owners-section">
        <div className="rounded-[2rem] bg-white border border-slate-200 shadow-xl grid lg:grid-cols-2 gap-8 lg:gap-12 items-center overflow-hidden">
          <div className="relative order-2 lg:order-1 h-72 sm:h-96 lg:h-full lg:min-h-[420px]">
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_yard-sale-finder-4/artifacts/khdbu26l_IMG_7388.JPG"
              alt="The owners of Yard-skip"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="owners-photo"
            />
          </div>
          <div className="order-1 lg:order-2 p-8 sm:p-12 lg:pl-0">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-mono uppercase tracking-widest font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" /> Locally owned &amp; operated
            </span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-5 leading-tight">
              Meet Dave &amp; Charlotte Skala
            </h2>
            <p className="text-base text-slate-600 mt-4 leading-relaxed max-w-lg">
              <strong>Dave and Charlotte Skala</strong> enjoy yard sales as much as you do. After years of
              hassles trying to enjoy going to them — and seeing all the headaches that come with it — Dave
              decided to create an easier way. We hope this helps you, whether you're a seller or someone who
              likes to cruise the neighborhoods looking for awesome deals!
            </p>
          </div>
        </div>
      </section>

      <WhatsNew />

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-slate-200 pt-8">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Yard-skip" className="w-8 h-8 rounded-lg" />
            <span className="font-display font-extrabold">Yard-<span className="text-ys-sky">skip</span></span>
          </div>
          <p className="text-sm text-slate-400">© {new Date().getFullYear()} Yard-skip · yard-skip.com</p>
          <div className="flex items-center gap-3">
            <SiteReviewButton />
            <TellAFriend url={window.location.origin} label="Tell a Friend" />
          </div>
        </div>
        <div className="mt-6 flex justify-center border-t border-slate-100 pt-6" data-testid="footer-admin-area">
          <AdminSignIn />
        </div>
      </footer>
    </div>
  );
}
